import { useEffect, useMemo, useRef } from 'react';

import { serveAppendDeactivatedAccountToContributorList } from '../../../common/utils/experiments';
import { recordMinedUsers } from '../../../controllers/store/search/filters/contributor/mined-deactivated-users';
import {
	fetchConfluenceAuthors,
	getConfluenceAuthorHydrationIds,
} from './fetch-confluence-authors';

type SearchResultEdge = Parameters<typeof getConfluenceAuthorHydrationIds>[0][number];

type UseConfluenceAuthorHydrationProps = {
	edges: ReadonlyArray<SearchResultEdge>;
	cloudId?: string;
	aggAbsoluteUrl?: string;
	logError: (error: unknown) => void;
	product?: Parameters<typeof fetchConfluenceAuthors>[0]['product'];
};

/**
 * Hydrates Confluence page/blogpost authors after the main search results render so the optional
 * Contributor-filter data cannot extend the search results' VC90 critical path.
 */
export const useConfluenceAuthorHydration = ({
	edges,
	cloudId,
	aggAbsoluteUrl,
	logError,
	product,
}: UseConfluenceAuthorHydrationProps): void => {
	const isEnabled = serveAppendDeactivatedAccountToContributorList();
	const releaseMinedUsersRef = useRef<(() => void) | undefined>();
	const { pageIds, blogPostIds } = useMemo(
		() =>
			isEnabled
				? getConfluenceAuthorHydrationIds(edges)
				: {
						pageIds: [],
						blogPostIds: [],
					},
		[edges, isEnabled],
	);

	useEffect(() => {
		if (!isEnabled || (pageIds.length === 0 && blogPostIds.length === 0)) {
			releaseMinedUsersRef.current?.();
			releaseMinedUsersRef.current = undefined;
			return undefined;
		}

		let isDisposed = false;

		void fetchConfluenceAuthors({
			pageIds,
			blogPostIds,
			cloudId,
			aggAbsoluteUrl,
			logError,
			product,
		}).then((users) => {
			if (!isDisposed) {
				const releasePreviousMinedUsers = releaseMinedUsersRef.current;
				releaseMinedUsersRef.current = recordMinedUsers(users);
				releasePreviousMinedUsers?.();
			}
		});

		return () => {
			isDisposed = true;
		};
	}, [aggAbsoluteUrl, blogPostIds, cloudId, isEnabled, logError, pageIds, product]);

	useEffect(
		() => () => {
			releaseMinedUsersRef.current?.();
		},
		[],
	);
};
